import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { values } from "lodash";
import { EditActions, editActions } from "../../actions/editActions";
import { nameofFactory, hasWriteAccess, isEmptyString } from "../../utils/utils";
import Editor from "../../components/Editing/Editor";
import NumberField from "../../components/Editing/NumberField";
import TextField from "../../components/Editing/TextField";
import DropdownField from "../../components/Editing/DropdownField";
import { DropdownOption, getDomainGroupNames } from "../../utils/pdApiUtils";

type StateProps = {
  editedEnumeration: Api.AnyEnumeration;
  storedEnumeration: Api.AnyEnumeration;
  pdApi: State.PdApi;
  activeUser: Api.User | undefined;
};

type DispatchProps = {
  editActions: typeof editActions;
};

type OwnProps = {
  toggleModal: () => void;
  parentMenu?: "Lift" | "Enumerations";
};

const faultComponentsFields = nameofFactory<Api.FaultComponentEnumeration>();

export type EditFaultComponentCommonProps = StateProps & DispatchProps & OwnProps;

const isFieldValid = (
  field: string,
  editedFaultComponent: Api.FaultComponentEnumeration,
  pdApi: State.PdApi,
  parentMenu?: "Lift" | "Enumerations"
): boolean => {
  switch (field) {
    case faultComponentsFields("enumValue"): {
      if (editedFaultComponent.enumValue < 0) return false;
      if (!Number.isInteger(editedFaultComponent.enumValue)) return false;
      // Check for global uniqueness across ALL fault components (regardless of domain)
      return pdApi.faultComponents.find(
        faultComponent =>
          faultComponent.id !== editedFaultComponent.id &&
          faultComponent.enumValue === editedFaultComponent.enumValue
      )
        ? false
        : true;
    }
    case faultComponentsFields("enumName"): {
      const { enumName } = editedFaultComponent;
      if (isEmptyString(enumName)) {
        return false;
      }
      // Get the domain group ID for the current fault component
      const currentDomainGroupId = editedFaultComponent.domainGroup?.id;

      // Check uniqueness only within the same domain group
      const duplicate = pdApi.faultComponents.find(
        faultComponent => {
          if (faultComponent.id === editedFaultComponent.id) {
            return false; // Skip self
          }
          // Check if names match
          const namesMatch = faultComponent.enumName?.trim().toLowerCase() ===
            editedFaultComponent.enumName?.trim().toLowerCase();
          
          if (!namesMatch) {
            return false; // Names don't match, not a duplicate
          }

          // Names match - check if in same domain group
          const otherDomainGroupId = faultComponent.domainGroup?.id;

          // If either domain group is undefined, treat as same group (conservative approach)
          if (currentDomainGroupId === undefined || otherDomainGroupId === undefined) {
            return true; // Block the duplicate to be safe
          }

          return currentDomainGroupId === otherDomainGroupId;
        }
      );
      
      return duplicate ? false : true;
    }
    case faultComponentsFields("domainGroup"): {
      if (parentMenu === "Lift") {
        const liftGroupId = values(pdApi.domainGroups).find(
          domainGroup => domainGroup.enumName === "Lift"
        )?.id;
        return !!liftGroupId && editedFaultComponent.domainGroup?.id === liftGroupId;
      }
      const selectedGroupId = editedFaultComponent.domainGroup?.id || -1;
      const selectedGroupName = pdApi.domainGroups[selectedGroupId]?.enumName?.toLowerCase();
      if (selectedGroupName === "unknown") {
        return false;
      }
      return selectedGroupId > 0;
    }
    case faultComponentsFields("faultRangeMin"): {
      const { faultRangeMin } = editedFaultComponent;
      return faultRangeMin >= 0;
    }
    case faultComponentsFields("faultRangeMax"): {
      const { faultRangeMin, faultRangeMax } = editedFaultComponent;
      return faultRangeMax >= 0 && faultRangeMax >= faultRangeMin;
    }
    case faultComponentsFields("name"): {
      const { name } = editedFaultComponent;
      if (isEmptyString(name)) {
        return false;
      }
      return true;
    }
    default:
      return true;
  }
};

const isAllFieldsValid = (
  editedFaultComponent: Api.FaultComponentEnumeration,
  pdApi: State.PdApi,
  parentMenu?: "Lift" | "Enumerations"
): boolean => {
  const fieldsToValidate: (keyof Api.FaultComponentEnumeration)[] = [
    "enumValue",
    "enumName",
    "domainGroup",
    "faultRangeMin",
    "faultRangeMax",
    "name"
  ];
  for (const key of fieldsToValidate) {
    if (!isFieldValid(key, editedFaultComponent, pdApi, parentMenu)) {
      return false;
    }
  }
  return true;
};

export function EditFaultComponentCommonComponent(props: EditFaultComponentCommonProps): JSX.Element {
  const { editedEnumeration, storedEnumeration, activeUser, toggleModal, pdApi, editActions } = props;
  const editedFaultComponent = editedEnumeration as Api.FaultComponentEnumeration;
  const isLiftMenu = props.parentMenu === "Lift";
  const isNew = !storedEnumeration;

  const liftDomainGroupId = React.useMemo(
    () =>
      values(pdApi.domainGroups).find(domainGroup => domainGroup.enumName === "Lift")?.id,
    [pdApi.domainGroups]
  );

  const domainGroupOptions = React.useMemo(() => getDomainGroupNames(pdApi), [pdApi]);
  
  let filteredDomainOptions: DropdownOption[];
  
  if (activeUser && activeUser.roles.some(role => role.name === "admin")) {
    filteredDomainOptions = domainGroupOptions;
  } else {
    filteredDomainOptions = domainGroupOptions.filter(domainOption => {
      const groupName: Api.GroupName = domainOption.text as Api.GroupName;
      return hasWriteAccess(activeUser, groupName);
    });
    if (editedFaultComponent.domainGroup?.id === 1 && filteredDomainOptions.length > 0) {
      const defaultDomainGroupId = filteredDomainOptions[0].value;
      const domainGroup = pdApi.domainGroups[defaultDomainGroupId];
      editActions.setEdited({ ...editedFaultComponent, domainGroup });
    }
  }

  // Show all domain group options for existing records (matching Parameter Category line 107)
  if (!isNew) {
    filteredDomainOptions = domainGroupOptions;
  }

  const domainGroupId = isLiftMenu
    ? liftDomainGroupId
    : editedFaultComponent.domainGroup?.id;

  // Determine the group name based on the selected domain group
  const groupName: Api.GroupName | undefined = React.useMemo(
    () =>
      values(pdApi.domainGroups).find(
        (domainGroup: Api.DomainGroup) => domainGroup.id === domainGroupId
      )?.enumName as Api.GroupName,
    [domainGroupId, pdApi.domainGroups]
  );

  // Calculate permission based on the selected domain group
  const permission = hasWriteAccess(
    activeUser,
    groupName === "Lift" ? ["Lift", "LIO"] : groupName
  );

  React.useEffect(() => {
    // In the Lift menu, force the domain group to Lift.
    if (!isLiftMenu || !liftDomainGroupId) {
      return;
    }

    if (editedFaultComponent.domainGroup?.id === liftDomainGroupId) {
      return;
    }

    editActions.setEdited({
      ...editedFaultComponent,
      domainGroup: { id: liftDomainGroupId, url: "" }
    });
  }, [editActions, editedFaultComponent, isLiftMenu, liftDomainGroupId]);

  const fieldValid = React.useCallback(
    (field: string) => isFieldValid(field, editedFaultComponent, pdApi, props.parentMenu),
    [editedFaultComponent, pdApi, props.parentMenu]
  );

  const allFieldsValid = React.useMemo(
    () => isAllFieldsValid(editedFaultComponent, pdApi, props.parentMenu),
    [editedFaultComponent, pdApi, props.parentMenu]
  );

  return (
    <Editor
      itemName={"fault component"}
      originalObject={storedEnumeration}
      editedObject={editedEnumeration}
      hasWritePermission={permission}
      isValid={allFieldsValid}
      cancel={editActions.cancelEditing}
      update={() => editActions.update("faultComponents")}
      create={() => editActions.add("faultComponents")}
      remove={{ callback: toggleModal }}
    >
      <NumberField
        id="code"
        label="Code"
        value={editedFaultComponent.enumValue}
        isError={!fieldValid(faultComponentsFields("enumValue"))}
        readonly={!isNew || !permission}
        errorMessage="The Code must be unique."
        onChange={enumValue => editActions.setEdited({ ...editedFaultComponent, enumValue })}
      />
      <TextField
        id="enumeration"
        label="Enumeration"
        value={editedFaultComponent.enumName}
        isError={!fieldValid(faultComponentsFields("enumName"))}
        errorMessage={
          !isEmptyString(editedFaultComponent.enumName)
            ? "Enumeration is already used as root fault component in the same domain."
            : ""
        }
        onChange={enumName => editActions.setEdited({ ...editedFaultComponent, enumName: enumName.trim() })}
        readonly={!isNew || !permission}
      />
      <TextField
        id="comment"
        label="Comment"
        value={editedFaultComponent.comment}
        onChange={comment => editActions.setEdited({ ...editedFaultComponent, comment: comment.trim() })}
        readonly={!permission}
      />
      <div className="spaced-flex-row">
        <NumberField
          id="minRange"
          label="Min Range"
          value={editedFaultComponent.faultRangeMin}
          isError={!fieldValid(faultComponentsFields("faultRangeMin"))}
          onChange={faultRangeMin => editActions.setEdited({ ...editedFaultComponent, faultRangeMin })}
          readonly={!permission}
        />
        <NumberField
          id="maxRange"
          label="Max Range"
          value={editedFaultComponent.faultRangeMax}
          isError={!fieldValid(faultComponentsFields("faultRangeMax"))}
          onChange={faultRangeMax => editActions.setEdited({ ...editedFaultComponent, faultRangeMax })}
          readonly={!permission}
        />
      </div>
      {!fieldValid(faultComponentsFields("faultRangeMax")) && (
        <p className="errorMessageMargin">
          The minimum value must be less than or equal to the maximum value, and vice versa.
        </p>
      )}
      <TextField
        id="name"
        label="Name (en-US)"
        value={editedFaultComponent.name}
        isError={!fieldValid(faultComponentsFields("name"))}
        onChange={name => editActions.setEdited({ ...editedFaultComponent, name: name.trim() })}
        readonly={!permission}
      />
      {!isLiftMenu && (
        <DropdownField
          id="domainGroup"
          label="Domain Group"
          value={domainGroupId}
          options={filteredDomainOptions}
          isError={!fieldValid(faultComponentsFields("domainGroup"))}
          errorMessage={"Fault component cannot be created for Unknown Domain Group."}
          search={true}
          disabled={
            !isNew ||
            filteredDomainOptions.length === 1
          }
          onChange={groupId => {
            const domainGroup = { id: groupId, url: "" };
            editActions.setEdited({
              ...editedFaultComponent,
              domainGroup
            });
          }}
        />
      )}
      {!permission && (
        <p className="color-red">Your role doesn't have necessary rights</p>
      )}
    </Editor>
  );
};

const mapStateToProps = (state: State.Root) => ({
  editedEnumeration: state.editView.editedObject,
  storedEnumeration: state.editView.storedObject,
  pdApi: {
    faultComponents: state.pdApi.faultComponents,
    domainGroups: state.pdApi.domainGroups,
    domains: state.pdApi.domains
  },
  activeUser: state.main.activeUser
});

const mapDispatchToProps = (dispatch: Dispatch<EditActions>) => ({
  editActions: bindActionCreators({ ...editActions }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(EditFaultComponentCommonComponent);
